import { Deck, SlideContent, StakeholderType } from '../types';
import { DEFAULT_THEME } from '../constants';

// Function to parse CSV content with proper handling of quoted fields
const parseCSVLine = (line: string): string[] => {
  const columns: string[] = [];
  let current = '';
  let inQuotes = false;
  
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    
    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      columns.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  
  // Add the last column
  columns.push(current.trim());
  
  return columns;
};

// Function to parse CSV content directly from small deck data.csv
const parseCSVData = (csvText: string) => {
  const lines = csvText.trim().split('\n');
  const csvData: any[] = [];
  
  // Skip header and empty lines
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line || line === ',,,,,') continue;
    
    const columns = parseCSVLine(line);
    if (columns.length >= 6 && columns[0] && columns[1]) {
      csvData.push({
        slide: columns[0],
        sharedStoryArc: columns[1],
        patient: columns[2],
        caregiver: columns[3], 
        clinician: columns[4],
        business: columns[5]
      });
    }
  }
  
  return csvData;
};

// Load CSV data from the actual file
const loadCSVFromFile = (): any[] => {
  // CSV content from small deck data.csv
  const csvContent = `Slide,Shared Story Arc,Patient,Caregiver,Clinician,Businesses / Platforms,
1,Healthcare does not happen during appointments alone.,Recovery continues after the visit ends.,Responsibility begins after discharge.,Outcomes depend on what happens between visits.,Retention and outcomes are shaped outside consultations.,
2,Caregiving is invisible healthcare work.,Family becomes part of recovery.,"Caregivers coordinate medicines, symptoms, appointments, and updates every day.",Families already influence adherence and recovery.,Existing systems ignore caregiver-driven continuity.,
3,But caregivers and patients are unsupported.,Questions remain unanswered. Isolation grows.,Information becomes scattered and stressful to manage.,Fragmented updates reduce visibility and safety.,"Fragmentation causes churn, poor engagement, and weaker outcomes.",
4,HealthyMitra creates a private care circle.,Stay connected to trusted people safely.,Coordinate care in one structured space.,Receive structured longitudinal communication instead of fragmented updates.,Create continuity infrastructure around patients and families.,
5,Consent stays central.,Stay in control of information sharing.,Participate only with explicit permission.,Maintain professional boundaries and accountability.,"Deploy privacy-conscious, auditable communication systems.",
6,Better context improves care.,Symptoms and concerns are easier to communicate over time.,Daily observations become clinically useful.,Better decisions emerge from richer longitudinal context.,AI becomes less blind when grounded in structured human context.,
7,Continuous support improves outcomes.,Feel supported between consultations.,Escalate concerns earlier and more confidently.,Earlier intervention improves patient safety and trust.,Better continuity improves retention and engagement.,
8,Clinicians remain central.,Access trusted professionals beyond isolated appointments.,Know someone is overseeing care when needed.,"Strengthen patient relationships, outcomes, and continuity. Earn through supervised oversight.",Expand continuous-care participation without replacing professionals.,
9,"Built for trust, safety, and accountability.",Trust how information is handled.,Trust that actions are visible and auditable.,"Trust source traceability, consent governance, and clinical oversight.",Trust institution-aligned infrastructure and governance.,
10,No one should feel alone between consultations.,Support continues after the visit.,Caregiving becomes less isolating.,Better continuity leads to better care relationships.,Healthcare relationships become continuous instead of episodic.,`;

  return parseCSVData(csvContent);
};

// Create slides from CSV data
const createSlidesFromCSV = (csvData: any[], stakeholder: StakeholderType): SlideContent[] => {
  const slides: SlideContent[] = [];
  
  const stakeholderColumnMap = {
    'patient': 'patient',
    'caregiver': 'caregiver', 
    'clinician': 'clinician',
    'mental-health': 'business', // Map mental-health to business column
    'enterprise': 'business'
  };
  
  const stakeholderSubtitleMap = {
    'patient': 'Patient Perspective',
    'caregiver': 'Caregiver Perspective',
    'clinician': 'Clinician Perspective', 
    'mental-health': 'Mental Health & Chronic Care Perspective',
    'enterprise': 'Business Perspective'
  };
  
  csvData.forEach((row, index) => {
    const columnKey = stakeholderColumnMap[stakeholder];
    const mainContent = row[columnKey];
    
    if (mainContent && mainContent.trim()) {
      const slideType = index === csvData.length - 1 ? 'closing-emotional' : 'statement';
      
      const slide: SlideContent = {
        id: `slide-${index + 1}-${stakeholder}`,
        type: slideType,
        title: row.sharedStoryArc,
        subtitle: stakeholderSubtitleMap[stakeholder],
        content: slideType === 'closing-emotional' ? {
          mainMessage: row.sharedStoryArc,
          callToAction: mainContent,
          emotionalTone: 'trust' as const
        } : {
          mainStatement: row.sharedStoryArc,
          supportingText: mainContent
        },
        speakerNotes: `Slide ${index + 1} from ${stakeholderSubtitleMap[stakeholder].toLowerCase()}`,
        visibleForStakeholders: [stakeholder]
      };
      
      slides.push(slide);
    }
  });
  
  return slides;
};

export const createSampleDeck = (stakeholder: StakeholderType): Deck => {
  const csvData = loadCSVFromFile();
  const slides = createSlidesFromCSV(csvData, stakeholder);
  
  const stakeholderNames = {
    'patient': 'Patient Care Materials',
    'caregiver': 'Caregiver Support Resources',
    'clinician': 'Clinician Resources',
    'mental-health': 'Mental Health & Chronic Care',
    'enterprise': 'Enterprise Partnership Materials'
  };
  
  return {
    id: `deck-${stakeholder}-${Date.now()}`,
    name: stakeholderNames[stakeholder],
    stakeholder,
    theme: DEFAULT_THEME,
    slides,
    createdAt: new Date(),
    updatedAt: new Date()
  };
};